import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, Download, Users, BookOpen, FileBadge, Gamepad2, Trophy, ClipboardList, Video, ListChecks, Undo2, ChevronLeft, ChevronRight, Wrench, AlertTriangle, UploadCloud, Send, FileDown, RotateCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';

const TABS = [
    { key: 'learner', label: 'Learner Progress', icon: Users },
    { key: 'course', label: 'Course Completion', icon: BookOpen },
    { key: 'video', label: 'Video Report', icon: Video },
    { key: 'quiz', label: 'Quiz Report', icon: ListChecks },
    { key: 'cert', label: 'Certifications', icon: FileBadge },
    { key: 'game', label: 'Game Engagement', icon: Gamepad2 },
    { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { key: 'all', label: 'All Learners', icon: ClipboardList },
    { key: 'rollback', label: 'Points Rollback', icon: Undo2 },
];

export default function AdminReports() {
    const [active, setActive] = useState('learner');
    const [filters, setFilters] = useState({ department: '', region: '', role: '', days: '', date_from: '', date_to: '', course_id: '', language: '', mobile: '' });
    const [filterOpts, setFilterOpts] = useState({ departments: [], regions: [], roles: [], courses: [], languages: [] });

    useEffect(() => { api.get('/admin/filters/options').then((r) => setFilterOpts(r.data)); }, []);

    return (
        <div data-testid="admin-reports-page">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Insights</div>
                    <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1 flex items-center gap-2"><BarChart3 className="h-7 w-7 text-brand-black" /> Reports</h1>
                    <p className="text-sm text-slate-500 mt-1">Multi-dimensional reports. Filter, sort and export to CSV.</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-slate-200 p-1 inline-flex mb-4" data-testid="reports-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key} onClick={() => setActive(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition ${active === t.key ? 'bg-brand-black text-brand-yellow' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid={`reports-tab-${t.key}`}
                    >
                        <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap gap-2 mb-5">
                <Sel label="Department" v={filters.department} on={(v) => setFilters({ ...filters, department: v })} options={filterOpts.departments} t="report-dept-filter" />
                <Sel label="Region" v={filters.region} on={(v) => setFilters({ ...filters, region: v })} options={filterOpts.regions} t="report-region-filter" />
                {active === 'learner' && (
                    <Sel label="Role" v={filters.role} on={(v) => setFilters({ ...filters, role: v })} options={filterOpts.roles} t="report-role-filter" />
                )}
                {active === 'game' && (
                    <Sel label="Window" v={filters.days} on={(v) => setFilters({ ...filters, days: v })} options={['7', '30', '90']} t="report-days-filter" />
                )}
                {(active === 'learner' || active === 'course' || active === 'all') && (
                    <>
                        <select value={filters.course_id} onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white max-w-[240px] truncate" data-testid="report-course-filter">
                            <option value="">All Courses</option>
                            {(filterOpts.courses || []).filter((c) => !filters.language || c.language === filters.language).map((c) => <option key={c.id} value={c.id}>{c.title} ({(c.language || 'en').toUpperCase()})</option>)}
                        </select>
                        <Sel label="Language" v={filters.language} on={(v) => setFilters({ ...filters, language: v, course_id: '' })} options={filterOpts.languages} t="report-language-filter" />
                    </>
                )}
                {(active === 'video' || active === 'quiz') && (
                    <Sel label="Language" v={filters.language} on={(v) => setFilters({ ...filters, language: v })} options={filterOpts.languages} t="report-language-filter" />
                )}
                {(active === 'learner' || active === 'all') && (
                    <input type="text" inputMode="numeric" value={filters.mobile}
                        onChange={(e) => setFilters({ ...filters, mobile: e.target.value })}
                        placeholder="Mobile number"
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white w-40" data-testid="report-mobile-filter" />
                )}
                <div className="flex items-center gap-1.5" data-testid="report-date-range">
                    <label className="text-xs font-semibold text-slate-500">From</label>
                    <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="px-2.5 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="report-date-from" />
                    <label className="text-xs font-semibold text-slate-500">To</label>
                    <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="px-2.5 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="report-date-to" />
                </div>
                <button onClick={() => setFilters({ department: '', region: '', role: '', days: '', date_from: '', date_to: '', course_id: '', language: '', mobile: '' })} className="text-xs text-slate-600 hover:text-slate-900 ml-1" data-testid="reports-clear-btn">Clear filters</button>
            </div>

            {active === 'learner' && <LearnerProgressReport filters={filters} />}
            {active === 'course' && <CourseCompletionReport filters={filters} />}
            {active === 'video' && <VideoSummaryReport filters={filters} />}
            {active === 'quiz' && <QuizSummaryReport filters={filters} />}
            {active === 'cert' && <CertReport filters={filters} />}
            {active === 'game' && <GameReport filters={filters} />}
            {active === 'leaderboard' && <LeaderboardReport filters={filters} />}
            {active === 'all' && <AllLearnersReport filters={filters} />}
            {active === 'rollback' && <RollbackReport />}
        </div>
    );
}

function Sel({ label, v, on, options, t }) {
    return (
        <select value={v} onChange={(e) => on(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white capitalize" data-testid={t}>
            <option value="">All {label}</option>
            {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

function Pager({ page, pageSize, total, onPage }) {
    const from = total === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(total, (page + 1) * pageSize);
    const pages = Math.max(1, Math.ceil(total / pageSize));
    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-white" data-testid="report-pager">
            <div className="text-xs font-semibold text-slate-600" data-testid="report-pager-info">
                {total === 0 ? 'No records' : `Showing ${from}–${to} of ${total.toLocaleString()}`}
            </div>
            <div className="flex items-center gap-2">
                <button disabled={page <= 0} onClick={() => onPage(page - 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ring-1 ring-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid="report-pager-prev">
                    <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <span className="text-xs text-slate-500" data-testid="report-pager-page">Page {page + 1} / {pages}</span>
                <button disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ring-1 ring-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid="report-pager-next">
                    Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function withDates(params, filters) {
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    return params;
}

function fmtDate(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}

function csvCell(c, r) {
    // Prefer an explicit CSV accessor; else a text-returning render; else the raw key.
    if (c.csv) return c.csv(r);
    if (c.render) {
        const v = c.render(r);
        // A render that returns JSX (React element) is an object → stringifies to
        // "[object Object]". In that case fall back to the raw field value (Point 65).
        if (v == null || typeof v === 'object') return r[c.key] ?? '';
        return v;
    }
    return r[c.key] ?? '';
}

function downloadCSV(rows, columns, filename) {    const header = columns.map((c) => c.label).join(',') + '\n';
    const body = rows.map((r) => columns.map((c) => `"${String(csvCell(c, r)).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

function ReportTable({ rows, columns, filename, testid, total, page, pageSize, onPage, onExportAll, exporting }) {
    const paginated = typeof onPage === 'function';
    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-600">
                    {paginated ? `${(total || 0).toLocaleString()} record${total === 1 ? '' : 's'}` : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
                </div>
                <button onClick={onExportAll || (() => downloadCSV(rows, columns, filename))} disabled={exporting} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold disabled:opacity-60" data-testid="report-export-btn">
                    <Download className="h-3.5 w-3.5" /> {exporting ? 'Preparing…' : (onExportAll ? 'Export CSV (all)' : 'Export CSV')}
                </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-600">
                        <tr>{columns.map((c) => <th key={c.key} className={`py-3 px-4 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((r, i) => (
                            <tr key={r.user_id || r.course || r.certificate_id || r.game || i} className="hover:bg-slate-50">
                                {columns.map((c) => <td key={c.key} className={`py-2.5 px-4 ${c.align === 'right' ? 'text-right' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={columns.length} className="py-12 text-center text-sm text-slate-500">No data for current filters.</td></tr>}
                    </tbody>
                </table>
            </div>
            {paginated && <Pager page={page} pageSize={pageSize} total={total} onPage={onPage} />}
        </div>
    );
}

function LearnerProgressReport({ filters }) {
    const PAGE = 50;
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [exporting, setExporting] = useState(false);

    const buildParams = (limit, skip) => {
        const params = {};
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        if (filters.role) params.role = filters.role;
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        if (filters.mobile) params.mobile = filters.mobile;
        withDates(params, filters);
        params.limit = limit; params.skip = skip;
        return params;
    };

    useEffect(() => { setPage(0); }, [filters]);
    useEffect(() => {
        api.get('/admin/reports/learner-progress', { params: buildParams(PAGE, page * PAGE) }).then((r) => {
            setRows(r.data.rows || []); setTotal(r.data.total || 0);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page]);

    const cols = [
        { key: 'full_name', label: 'Learner', render: (r) => (
            <Link to={`/admin/learners/${r.user_id}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-learner-link-${r.user_id}`}>{r.full_name}</Link>
        ) },
        { key: 'mobile', label: 'Mobile' },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'enrollments', label: 'Started', align: 'right' },
        { key: 'in_progress', label: 'In Progress', align: 'right' },
        { key: 'completed', label: 'Completed', align: 'right' },
        { key: 'certificates', label: 'Certificates', align: 'right' },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'points', label: 'Points', align: 'right' },
    ];

    const exportAll = async () => {
        setExporting(true);
        try {
            const r = await api.get('/admin/reports/learner-progress', { params: buildParams(0, 0) });
            downloadCSV(r.data.rows || [], cols, 'learner-progress.csv');
        } finally { setExporting(false); }
    };

    return (
        <div className="space-y-5">
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="font-display font-bold mb-3 text-slate-900">Top 10 by avg progress {page > 0 ? '(this page)' : ''}</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={rows.slice(0, 10)} layout="vertical" margin={{ left: 30 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="full_name" tick={{ fontSize: 11 }} width={120} />
                            <Tooltip />
                            <Bar dataKey="avg_progress" fill="#231F20" radius={[0, 8, 8, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="font-display font-bold mb-3 text-slate-900">Points distribution (top 10)</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={rows.slice(0, 10).sort((a, b) => b.points - a.points)} margin={{ bottom: 10 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis dataKey="full_name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="points" fill="#FFF200" stroke="#231F20" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <ReportTable rows={rows} columns={cols} testid="learner-progress-table"
                total={total} page={page} pageSize={PAGE} onPage={setPage} onExportAll={exportAll} exporting={exporting} />
        </div>
    );
}

function CourseCompletionReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = withDates({}, filters);
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/course-completion', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course', label: 'Course' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'total', label: 'Learners Started', align: 'right' },
        { key: 'completed', label: 'Completed', align: 'right' },
        { key: 'certificates', label: 'Certificates', align: 'right' },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'completion_rate', label: 'Completion Rate', align: 'right', render: (r) => `${r.completion_rate}%` },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Completion rate by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total" fill="#231F20" name="Started" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="completed" fill="#FFF200" stroke="#231F20" name="Completed" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="course-completion.csv" testid="course-completion-table" />
        </div>
    );
}

function VideoSummaryReport({ filters }) {
    // Point 52 (VIDEO only): Course ID · Title · started · in progress · completed
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/video-summary', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course_id', label: 'Course ID' },
        { key: 'course_title', label: 'Course Title' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'started', label: 'Total User Started', align: 'right' },
        { key: 'in_progress', label: 'In Progress', align: 'right' },
        { key: 'completed', label: 'Total User Completed', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Video engagement by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course_title" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="started" fill="#231F20" name="Started" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="in_progress" fill="#94A3B8" name="In Progress" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="completed" fill="#FFF200" stroke="#231F20" name="Completed" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="video-report.csv" testid="video-summary-table" />
        </div>
    );
}

function QuizSummaryReport({ filters }) {
    // Point 52 (QUIZ only): Course ID · Title · started quiz · in progress quiz · passed · failed
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/quiz-summary', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course_id', label: 'Course ID' },
        { key: 'course_title', label: 'Course Title' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'started_quiz', label: 'Total Started Quiz', align: 'right' },
        { key: 'in_progress_quiz', label: 'In Progress Quiz', align: 'right' },
        { key: 'passed', label: 'Total Passed Quiz', align: 'right' },
        { key: 'failed', label: 'Total Failed Quiz', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-1 text-slate-900">Quiz outcomes by course</h3>
                <p className="text-xs text-slate-500 mb-3">In-progress quiz = learners who started the course but haven't attempted the quiz yet.</p>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course_title" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="passed" fill="#22C55E" name="Passed" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="failed" fill="#EF4444" name="Failed" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="in_progress_quiz" fill="#94A3B8" name="In Progress" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="quiz-report.csv" testid="quiz-summary-table" />
        </div>
    );
}

function CertReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        withDates(params, filters);
        api.get('/admin/reports/certifications', { params }).then((r) => setRows(r.data.rows || []));
    }, [filters]);
    const cols = [
        { key: 'course', label: 'Course' },
        { key: 'count', label: 'Certifications', align: 'right' },
        { key: 'avg_score', label: 'Avg Score', align: 'right', render: (r) => `${r.avg_score}%` },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Certifications by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                        <Pie data={rows} dataKey="count" nameKey="course" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={4}>
                            {rows.map((_, i) => <Cell key={i} fill={['#231F20', '#FFF200', '#E31E24', '#F47E1F', '#8B5CF6'][i % 5]} stroke="#fff" />)}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="certifications.csv" testid="cert-table" />
        </div>
    );
}

function GameReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = { days: filters.days || '30' };
        withDates(params, filters);
        api.get('/admin/reports/game-engagement', { params }).then((r) => setRows(r.data.rows || []));
    }, [filters]);
    const cols = [
        { key: 'game', label: 'Game', render: (r) => (
            <Link to={`/admin/reports/game/${r.game}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-game-link-${r.game}`}>{r.game}</Link>
        ) },
        { key: 'plays', label: 'Plays', align: 'right' },
        { key: 'unique_players', label: 'Unique Players', align: 'right' },
        { key: 'avg_score', label: 'Avg Score', align: 'right' },
        { key: 'total_points', label: 'Points Distributed', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Plays per game (last {filters.days || 30} days)</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="game" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="plays" fill="#231F20" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="unique_players" fill="#FFF200" stroke="#231F20" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="game-engagement.csv" testid="game-engagement-table" />
        </div>
    );
}


function LeaderboardReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get('/admin/reports/leaderboard', { params: withDates({ limit: 100 }, filters) }).then((r) => {
            const ranked = (r.data.rows || []).map((u, i) => ({ ...u, rank: i + 1 }));
            setRows(ranked);
        });
    }, [filters]);
    const cols = [
        { key: 'rank', label: '#', render: (r) => (
            <span className={`inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full text-xs font-bold ${r.rank <= 3 ? 'bg-brand-yellow text-brand-black' : 'bg-slate-100 text-slate-600'}`}>{r.rank}</span>
        ) },
        { key: 'name', label: 'Learner', render: (r) => (
            r.id
                ? <Link to={`/admin/learners/${r.id}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-leader-link-${r.id}`}>{r.full_name || r.name || '—'}</Link>
                : (r.full_name || r.name || '—')
        ) },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'points', label: 'Points', align: 'right', render: (r) => <span className="font-bold text-slate-900">{(r.points ?? 0).toLocaleString()}</span> },
    ];
    return (
        <div className="space-y-5" data-testid="leaderboard-report">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900 flex items-center gap-2"><Trophy className="h-5 w-5 text-brand-yellowDark" /> Top learners by points</h3>
                <p className="text-sm text-slate-500">Live ranking of trainees across the academy. Top 3 are highlighted.</p>
            </div>
            <ReportTable rows={rows} columns={cols} filename="leaderboard.csv" testid="leaderboard-table" />
        </div>
    );
}

function PointsCorrectionCard({ onDone }) {
    const [courseId, setCourseId] = useState('CRS-11F4F02F');
    const [lang, setLang] = useState('en');
    const [fromPts, setFromPts] = useState(50);
    const [toPts, setToPts] = useState(25);
    const [preview, setPreview] = useState(null);   // dry-run result
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const payload = () => ({ course_id: courseId.trim(), language: lang.trim(), from_points: Number(fromPts), to_points: Number(toPts) });
    const valid = courseId.trim() && Number(fromPts) > Number(toPts) && Number(toPts) >= 0;
    const reset = () => setPreview(null);

    const runPreview = async () => {
        setBusy(true); setPreview(null);
        try {
            const r = await api.post('/admin/points/correct-course-award', { ...payload(), dry_run: true });
            setPreview(r.data);
            if (!r.data.resolved_courses) toast.error('No course found for that Course ID / Course Code + language.');
            else if (!r.data.entries) toast.info('No learners were issued that amount for this course.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Preview failed');
        } finally { setBusy(false); }
    };

    const runApply = async () => {
        setBusy(true);
        try {
            const r = await api.post('/admin/points/correct-course-award', payload());
            toast.success(`Corrected ${r.data.entries} learner(s) — deducted ${r.data.points_deducted} pts total`);
            setConfirm(false); setPreview(null);
            onDone && onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Correction failed');
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="points-correction-card">
            <h3 className="font-display font-bold mb-1 text-slate-900 flex items-center gap-2"><Wrench className="h-5 w-5 text-amber-600" /> Correct over-awarded points</h3>
            <p className="text-sm text-slate-500">Reprice a course that granted the wrong points. Enter the <b>Course ID / Course Code</b> exactly as it appears in the All Learners report, pick the <b>Language</b>, and set From → To. Every learner issued the <b>From</b> value on that course has the excess deducted so it becomes the <b>To</b> value. Logged below and on each learner's profile. LMS records only — the Loyalty wallet is not changed. Always <b>Preview</b> first.</p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mt-4">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1 sm:col-span-2">Course ID / Code
                    <input value={courseId} onChange={(e) => { setCourseId(e.target.value); reset(); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono font-normal normal-case text-slate-800" data-testid="correction-course-id" />
                </label>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">Language
                    <input value={lang} onChange={(e) => { setLang(e.target.value); reset(); }} placeholder="en (blank = all)" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case text-slate-800" data-testid="correction-language" />
                </label>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">From (wrong)
                    <input type="number" value={fromPts} onChange={(e) => { setFromPts(e.target.value); reset(); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800" data-testid="correction-from-points" />
                </label>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex flex-col gap-1">To (correct)
                    <input type="number" value={toPts} onChange={(e) => { setToPts(e.target.value); reset(); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800" data-testid="correction-to-points" />
                </label>
            </div>
            <div className="mt-3">
                <button onClick={runPreview} disabled={busy || !valid} className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold disabled:opacity-50" data-testid="correction-preview-btn">{busy && !confirm ? 'Checking…' : 'Preview'}</button>
            </div>
            {preview && (
                <div className="mt-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 p-4" data-testid="correction-preview-result">
                    <div className="text-sm text-slate-700">
                        Resolved <b>{preview.resolved_courses}</b> course record(s) for <span className="font-mono">{preview.course_id}</span> · lang <b>{preview.language}</b> (matched by {preview.matched_by === 'course_code' ? 'Course Code' : 'Course ID'}).
                    </div>
                    <div className="text-sm text-slate-700 mt-1">
                        <b data-testid="correction-preview-entries">{preview.entries}</b> learner(s) were issued <b>{preview.from_points}</b> pts.
                        {preview.entries > 0 && <> Applying will deduct <b>{preview.delta_each}</b> each — <b className="text-rose-600" data-testid="correction-preview-total">−{preview.points_to_deduct}</b> pts total — leaving <b>{preview.to_points}</b> per learner.</>}
                    </div>
                    {preview.entries === 0 && preview.diagnostic && preview.diagnostic.length > 0 && (
                        <div className="mt-2 text-xs text-slate-600" data-testid="correction-diagnostic">
                            <div className="font-semibold mb-1">Points actually issued on this course:</div>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {preview.diagnostic.map((d, i) => (
                                    <li key={i}>{d.count} entr{d.count === 1 ? 'y' : 'ies'} of <b>{d.points}</b> pts <span className="text-slate-400">({d.ref_type})</span></li>
                                ))}
                            </ul>
                            <div className="mt-1 text-slate-500">Tip: set <b>From</b> to one of these values (per single award), then Preview again.</div>
                        </div>
                    )}
                    {preview.entries > 0 && (
                        <button onClick={() => setConfirm(true)} disabled={busy} className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="correction-apply-btn">Apply correction</button>
                    )}
                </div>
            )}
            {confirm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setConfirm(false)} data-testid="correction-confirm-modal">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" /> Confirm points correction</h3>
                        <p className="text-sm text-slate-600 mt-2">Deduct <b className="text-rose-600">{preview.points_to_deduct}</b> points across <b>{preview.entries}</b> learner(s) for <span className="font-mono">{preview.course_id}</span> · {preview.language} ({preview.from_points} → {preview.to_points}). This is logged and reversible only by re-awarding. Continue?</p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="correction-cancel-btn">Cancel</button>
                            <button onClick={runApply} disabled={busy} className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60" data-testid="correction-confirm-btn">{busy ? 'Working…' : `Deduct ${preview.points_to_deduct} pts`}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function refundRef(ref) {
    const r = String(ref || '').trim();
    return r.toUpperCase().startsWith('REFUND-') ? r : `REFUND-${r}`;
}

function parseReversalCsv(text) {
    const lines = String(text).split(/\r?\n/).filter((l) => l.trim().length);
    if (!lines.length) return [];
    const strip = (c) => c.trim().replace(/^"|"$/g, '');
    const header = lines[0].split(',').map((h) => strip(h).toLowerCase());
    const idx = { phone: header.indexOf('phone'), points: header.indexOf('points'), reference: header.indexOf('reference') };
    const hasHeader = idx.phone !== -1 || idx.points !== -1 || idx.reference !== -1;
    const dataLines = hasHeader ? lines.slice(1) : lines;
    return dataLines.map((line) => {
        const cells = line.split(',').map(strip);
        const get = (k, pos) => (hasHeader && idx[k] !== -1 ? (cells[idx[k]] ?? '') : (cells[pos] ?? ''));
        return { phone: get('phone', 0), points: get('points', 1), reference: get('reference', 2) };
    }).filter((r) => r.phone || r.reference);
}

function ZeaplReversalCard({ rollbackRows }) {
    const [token, setToken] = useState('');
    const [tokenStatus, setTokenStatus] = useState(null);
    const [parsed, setParsed] = useState([]);
    const [fileName, setFileName] = useState('');
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);
    const fileRef = useRef(null);

    const loadTokenStatus = () => api.get('/admin/loyalty/reversal/token').then((r) => setTokenStatus(r.data)).catch(() => {});
    useEffect(() => { loadTokenStatus(); }, []);

    const rowsValid = parsed.filter((r) => /^\d+$/.test(String(r.phone).trim()) && Number(r.points) > 0 && String(r.reference).trim());

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFileName(f.name); setResults(null);
        const text = await f.text();
        const rows = parseReversalCsv(text);
        setParsed(rows);
        if (!rows.length) toast.error('No rows found in the file.');
        else toast.success(`${rows.length} row(s) loaded from ${f.name}`);
    };

    const downloadSample = () => {
        const csv = 'phone,points,reference\n8887689675,50,TXN1789555590582618\n9970383542,25,TXN1789555590999111\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'zeapl-reversal-sample.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const exportFromRollback = () => {
        const src = (rollbackRows || []).filter((r) => String(r.mobile || '').match(/^\d+$/) && r.transaction_id && Number(r.points) > 0);
        if (!src.length) { toast.info('No rollback records with a mobile + transaction ID to export yet.'); return; }
        const csv = 'phone,points,reference\n' + src.map((r) => `${r.mobile},${r.points},${r.transaction_id}`).join('\n') + '\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'rollback-records-for-reversal.csv'; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${src.length} rollback record(s). Review, then upload to push.`);
    };

    const push = async () => {
        if (!rowsValid.length) { toast.error('No valid rows to push.'); return; }
        if (!token.trim() && !(tokenStatus?.present && !tokenStatus?.expired)) {
            toast.error('Paste the current reversal Bearer token first.'); return;
        }
        setBusy(true); setResults(null);
        try {
            const payload = { rows: rowsValid.map((r) => ({ phone: String(r.phone).trim(), points: Number(r.points), reference: String(r.reference).trim() })) };
            if (token.trim()) payload.token = token.trim();
            const r = await api.post('/admin/loyalty/reversal/push', payload);
            setResults(r.data);
            const c = r.data.counts || {};
            toast.success(`Pushed ${r.data.total}: ${c.success || 0} ok, ${c.failed || 0} failed, ${c.skipped || 0} skipped`);
            if (!r.data.configured) toast.info('Zeapl URL is not configured in this environment — calls were recorded as skipped.');
            loadTokenStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Push failed');
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-white rounded-2xl border border-indigo-200 p-5" data-testid="zeapl-reversal-card">
            <h3 className="font-display font-bold mb-1 text-slate-900 flex items-center gap-2"><RotateCcw className="h-5 w-5 text-indigo-600" /> Push reversals to Zeapl</h3>
            <p className="text-sm text-slate-500">Push rolled-back deductions to the Loyalty wallet as reverse credits. Upload a file of <b>phone, points, reference</b> — each is sent as <b>negative points</b>, <code>type: "C"</code>, with the reference prefixed <b>REFUND-</b>. Paste the current reversal Bearer token (used only for reversals).</p>

            <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Reversal Bearer token</label>
                <textarea value={token} onChange={(e) => setToken(e.target.value)} rows={2} placeholder="Paste the current reversal JWT (Bearer token)…" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono text-slate-800" data-testid="reversal-token-input" />
                {tokenStatus && (
                    <div className="text-xs mt-1" data-testid="reversal-token-status">
                        {tokenStatus.present
                            ? <span className={tokenStatus.expired ? 'text-rose-600' : 'text-emerald-600'}>Saved token {tokenStatus.masked} · {tokenStatus.expired ? 'EXPIRED — paste a fresh one' : `valid until ${tokenStatus.expires_at ? new Date(tokenStatus.expires_at).toLocaleString() : 'unknown'}`}</span>
                            : <span className="text-slate-400">No token saved yet. Paste one above.</span>}
                        {!tokenStatus.configured && <span className="text-amber-600"> · Zeapl URL not configured here (preview) — calls will be recorded as skipped.</span>}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold" data-testid="reversal-upload-btn"><UploadCloud className="h-4 w-4" /> Upload file</button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="reversal-file-input" />
                <button onClick={downloadSample} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold" data-testid="reversal-sample-btn"><FileDown className="h-4 w-4" /> Download sample</button>
                <button onClick={exportFromRollback} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold" data-testid="reversal-export-btn"><Download className="h-4 w-4" /> Export rollback records</button>
                {fileName && <span className="self-center text-xs text-slate-500" data-testid="reversal-file-name">{fileName} · {parsed.length} row(s)</span>}
            </div>

            {parsed.length > 0 && (
                <div className="mt-4 overflow-x-auto rounded-xl ring-1 ring-slate-200" data-testid="reversal-preview">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                            <tr><th className="text-left px-3 py-2">Phone</th><th className="text-right px-3 py-2">Points sent</th><th className="text-left px-3 py-2">Reference sent</th><th className="text-left px-3 py-2">Valid</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {parsed.slice(0, 100).map((r, i) => {
                                const ok = /^\d+$/.test(String(r.phone).trim()) && Number(r.points) > 0 && String(r.reference).trim();
                                return (
                                    <tr key={i} data-testid={`reversal-row-${i}`}>
                                        <td className="px-3 py-1.5 font-mono">{r.phone || '—'}</td>
                                        <td className="px-3 py-1.5 text-right font-bold text-rose-600">{Number(r.points) > 0 ? -Math.abs(Number(r.points)) : '—'}</td>
                                        <td className="px-3 py-1.5 font-mono text-xs text-slate-600">{r.reference ? refundRef(r.reference) : '—'}</td>
                                        <td className="px-3 py-1.5">{ok ? <span className="text-emerald-600">✓</span> : <span className="text-rose-500 text-xs">invalid</span>}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {parsed.length > 100 && <div className="px-3 py-2 text-xs text-slate-400">Showing first 100 of {parsed.length} rows.</div>}
                </div>
            )}

            {parsed.length > 0 && (
                <button onClick={push} disabled={busy || !rowsValid.length} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="reversal-push-btn">
                    <Send className="h-4 w-4" /> {busy ? 'Pushing…' : `Push ${rowsValid.length} reversal(s) to Zeapl`}
                </button>
            )}

            {results && (
                <div className="mt-4 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4" data-testid="reversal-results">
                    <div className="text-sm font-semibold text-slate-800">Pushed {results.total}: <span className="text-emerald-600">{results.counts?.success || 0} ok</span> · <span className="text-rose-600">{results.counts?.failed || 0} failed</span> · <span className="text-slate-500">{results.counts?.skipped || 0} skipped</span></div>
                    <div className="mt-2 max-h-56 overflow-y-auto">
                        <table className="min-w-full text-xs">
                            <thead className="text-slate-400 uppercase"><tr><th className="text-left py-1">Phone</th><th className="text-right">Points</th><th className="text-left px-2">Reference</th><th className="text-left">Status</th><th className="text-left">Message</th></tr></thead>
                            <tbody>
                                {results.results?.map((r, i) => (
                                    <tr key={i} data-testid={`reversal-result-${i}`}>
                                        <td className="py-1 font-mono">{r.phone}</td>
                                        <td className="text-right font-mono">{r.points}</td>
                                        <td className="px-2 font-mono">{r.reference}</td>
                                        <td className={r.status === 'success' ? 'text-emerald-600 font-semibold' : r.status === 'failed' ? 'text-rose-600 font-semibold' : 'text-slate-500'}>{r.status}</td>
                                        <td className="text-slate-500">{r.message || (r.http_status ? `HTTP ${r.http_status}` : '')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function RollbackReport() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ entries: 0, total_points: 0, affected_users: 0 });
    const [exporting, setExporting] = useState(false);
    const load = () => api.get('/admin/reports/rollback-log', { params: { limit: 200 } }).then((r) => {
        setRows(r.data.rows || []);
        setSummary(r.data.summary || { entries: 0, total_points: 0, affected_users: 0 });
    });
    useEffect(() => { load(); }, []);
    const cols = [
        { key: 'learner', label: 'Learner', render: (r) => (
            r.user_id
                ? <Link to={`/admin/learners/${r.user_id}`} className="font-semibold text-brand-blue hover:underline" data-testid={`rollback-learner-link-${r.user_id}`}>{r.learner}</Link>
                : (r.learner || '—')
        ) },
        { key: 'mobile', label: 'Mobile' },
        { key: 'course_id', label: 'Course ID' },
        { key: 'course_title', label: 'Course' },
        { key: 'transaction_id', label: 'Transaction ID', render: (r) => <span className="font-mono text-xs text-slate-600">{r.transaction_id || '—'}</span> },
        { key: 'type', label: 'Type' },
        { key: 'points', label: 'Points Deducted', align: 'right', render: (r) => <span className="font-bold text-rose-600">−{r.points}</span> },
        { key: 'awarded_at', label: 'Awarded On', render: (r) => fmtDate(r.awarded_at) },
        { key: 'rolled_back_at', label: 'Rolled Back On', render: (r) => fmtDate(r.rolled_back_at) },
    ];
    const exportAll = async () => {
        setExporting(true);
        try {
            const r = await api.get('/admin/reports/rollback-log', { params: { limit: 0 } });
            downloadCSV(r.data.rows || [], cols, 'points-rollback-log.csv');
        } finally { setExporting(false); }
    };
    return (
        <div className="space-y-4" data-testid="rollback-report">
            <PointsCorrectionCard onDone={load} />
            <ZeaplReversalCard rollbackRows={rows} />
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-1 text-slate-900 flex items-center gap-2"><Undo2 className="h-5 w-5 text-amber-600" /> Legacy points rollback log</h3>
                <p className="text-sm text-slate-500">Learners whose old <b>video-only</b> completion points (awarded before the quiz-pass rule) were deducted. Balances and other reports already reflect these deductions.</p>
                <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center"><div className="font-display font-extrabold text-2xl text-amber-700" data-testid="rollback-report-entries">{summary.entries.toLocaleString()}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">records</div></div>
                    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center"><div className="font-display font-extrabold text-2xl text-rose-600" data-testid="rollback-report-points">−{summary.total_points.toLocaleString()}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">points deducted</div></div>
                    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center"><div className="font-display font-extrabold text-2xl text-amber-700" data-testid="rollback-report-users">{summary.affected_users.toLocaleString()}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">learners</div></div>
                </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="rollback-report-table">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Showing {rows.length.toLocaleString()} of {summary.entries.toLocaleString()}</div>
                    <button onClick={exportAll} disabled={exporting} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold disabled:opacity-50" data-testid="rollback-report-export-btn">
                        <Download className="h-3.5 w-3.5" /> {exporting ? 'Exporting…' : 'Export CSV (all)'}
                    </button>
                </div>
                <div className="overflow-auto max-h-[60vh]">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-600">
                            <tr>{cols.map((c) => <th key={c.key} className={`py-3 px-4 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={i} className="hover:bg-slate-50" data-testid={`rollback-report-row-${i}`}>
                                    {cols.map((c) => <td key={c.key} className={`py-2.5 px-4 ${c.align === 'right' ? 'text-right' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={cols.length} className="py-12 text-center text-sm text-slate-500">No rollback records yet. Run the rollback from Admin → Users → Legacy Points Rollback.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function AllLearnersReport({ filters }) {
    const PAGE = 100;
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [rbTarget, setRbTarget] = useState(null);   // row pending rollback confirm
    const [rbBusy, setRbBusy] = useState(false);

    const buildParams = (limit, skip) => {
        const params = { limit };
        if (skip) params.skip = skip;
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        if (filters.mobile) params.mobile = filters.mobile;
        withDates(params, filters);
        return params;
    };

    const load = () => {
        api.get('/admin/reports/all-learners', { params: buildParams(PAGE, page * PAGE) }).then((r) => {
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        });
    };
    useEffect(() => { setPage(0); }, [filters]);
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters, page]);

    const runRollback = async () => {
        if (!rbTarget) return;
        setRbBusy(true);
        try {
            const r = await api.post('/admin/points/rollback-course', { user_id: rbTarget.user_id, course_id: rbTarget.course_id });
            toast.success(`Reset ${r.data.points} pts for ${rbTarget.learner_name} on ${r.data.course_title || rbTarget.course_name}`);
            setRbTarget(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Rollback failed'); }
        finally { setRbBusy(false); }
    };

    const cols = [
        { key: 'course_code', label: 'Course ID' },
        { key: 'learner_name', label: 'Learner' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'course_name', label: 'Course' },
        { key: 'date', label: 'Start Date', render: (r) => fmtDate(r.date) },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'started', label: 'Started', align: 'center' },
        { key: 'in_progress', label: 'In Progress', align: 'center' },
        { key: 'completed', label: 'Completed', align: 'center' },
        { key: 'certificate', label: 'Certificate', align: 'center' },
        { key: 'scored_pct', label: 'Scored %', align: 'right', render: (r) => `${r.scored_pct}%` },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'points_scored', label: 'Points Scored', align: 'right' },
        { key: 'points_earned_at', label: 'Points Earned On', render: (r) => fmtDate(r.points_earned_at) },
        { key: 'transaction_id', label: 'Transaction ID', render: (r) => <span className="font-mono text-xs text-slate-600">{r.transaction_id || '—'}</span> },
        { key: 'balance_points', label: 'Balance Points', align: 'right' },
        { key: '_action', label: 'Action', align: 'center', render: (r) => (
            (r.points_scored > 0)
                ? <button onClick={() => setRbTarget(r)} title="Reset this course's points for this learner"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 text-rose-600 ring-1 ring-rose-200 text-xs font-semibold hover:bg-rose-100"
                    data-testid={`all-learner-rollback-btn-${r.user_id}-${r.course_id}`}>
                    <Undo2 className="h-3.5 w-3.5" /> Rollback
                </button>
                : <span className="text-slate-300 text-xs">—</span>
        ) },
    ];

    const exportAll = async () => {
        setExporting(true);
        try {
            const r = await api.get('/admin/reports/all-learners', { params: buildParams(0, 0) });
            downloadCSV(r.data.rows || [], cols.filter((c) => c.key !== '_action'), 'all-learners.csv');
        } finally { setExporting(false); }
    };

    return (
        <div className="space-y-4" data-testid="all-learners-report">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-600" data-testid="all-learners-count">
                        {total.toLocaleString()} record{total === 1 ? '' : 's'} · export includes all {total.toLocaleString()}
                    </div>
                    <button onClick={exportAll} disabled={exporting}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold disabled:opacity-60"
                        data-testid="all-learners-export-btn">
                        <Download className="h-3.5 w-3.5" /> {exporting ? 'Preparing…' : 'Export CSV (all)'}
                    </button>
                </div>
                <div className="overflow-auto max-h-[68vh]">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 text-[11px] uppercase tracking-wider text-slate-600">
                            <tr>{cols.map((c) => <th key={c.key} className={`py-3 px-3 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={`${r.user_id}-${r.course_code}-${i}`} className="hover:bg-slate-50" data-testid={`all-learner-row-${i}`}>
                                    {cols.map((c) => <td key={c.key} className={`py-2.5 px-3 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={cols.length} className="py-12 text-center text-sm text-slate-500">No data for current filters.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <Pager page={page} pageSize={PAGE} total={total} onPage={setPage} />
            </div>

            {rbTarget && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !rbBusy && setRbTarget(null)} data-testid="confirm-course-rollback">
                    <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-lg text-slate-900">Reset points for this course?</h3>
                        <p className="text-sm text-slate-600 mt-2">
                            This removes the <b>{rbTarget.points_scored}</b> point(s) that <b>{rbTarget.course_name}</b> ({(rbTarget.language || 'en').toUpperCase()}) gave <b>{rbTarget.learner_name}</b> and lowers their balance. A full audit copy is kept and it shows up in the Points Rollback report. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setRbTarget(null)} disabled={rbBusy} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="course-rollback-cancel">Cancel</button>
                            <button onClick={runRollback} disabled={rbBusy} className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60" data-testid="course-rollback-confirm">
                                {rbBusy ? 'Working…' : `Reset ${rbTarget.points_scored} pts`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
